import React from 'react';
import {Platform, StyleSheet, View} from 'react-native';

import {FIELD_ICON_COLOR} from '../../../design-system/form/FormField';
import {field} from '../../../design-system/tokens';
import SearchImg from '../../../images/svg/icons/FieldSearch.svg';

import styles from './styles';

// Decorative search glyph at the leading edge of a searchable DropDownPicker box
// (DIS-1748; shared with SuburbPicker by DIS-1827). DropDownPicker has no
// leading-icon slot for the placeholder or the search input (its schema icon
// renders only for a selected item), so the glyph is drawn over the picker and
// the picker text is inset past it.
// The glyph sits field.paddingX (12, Figma) in from the field edge. TEXT_INSET
// replaces the library theme's 10px left padding on the box and search input
// (measured on react-native-dropdown-picker 5.4.6), so the text clears the glyph
// only while TEXT_INSET_STYLE still wins over that padding; re-check on any bump.
// SHORTCUT: GLYPH_SIZE 20 and GLYPH_GAP 10 are Figma "Loyalty Sign-Up Redesign"
// literals; 10 is off the space scale and no icon-size token exists. Ceiling: this
// glyph. Upgrade path: icon-size and spacing tokens under app/design-system/tokens/
// (DIS-1737 blocks them, DIS-1735 is the epic).
const GLYPH_SIZE = 20; // Figma search glyph
const GLYPH_INSET = field.paddingX; // Figma: field edge to glyph
const GLYPH_GAP = 10; // Figma: glyph to text
const TEXT_INSET = GLYPH_INSET + GLYPH_SIZE + GLYPH_GAP;
// Must stay a plain object, never a StyleSheet.create entry. On react-native-web
// the library spreads its theme style into a plain object, which renders as an
// inline padding-left:10px; an inline style beats a StyleSheet class, so only an
// inline inset wins over it.
export const TEXT_INSET_STYLE = {paddingLeft: TEXT_INSET};
export const PICKER_Z_INDEX = 500;

type Props = {
  isOpen: boolean;
  disabled?: boolean;
  testID: string;
};

export const PickerSearchGlyph = ({isOpen, disabled, testID}: Props) => (
  <View
    testID={testID}
    pointerEvents="none"
    accessibilityElementsHidden={true}
    importantForAccessibility="no-hide-descendants"
    aria-hidden={true}
    style={[
      glyphStyles.glyph,
      isOpen ? glyphStyles.glyphOpen : glyphStyles.glyphClosed,
      disabled ? glyphStyles.glyphDisabled : null,
    ]}>
    <SearchImg width={GLYPH_SIZE} height={GLYPH_SIZE} color={FIELD_ICON_COLOR} />
  </View>
);

export const pickerWrapperStyles = StyleSheet.create({
  // Keeps the picker's stacking order among its siblings. The library sets no
  // container zIndex on Android, so neither does the wrapper.
  wrapper: Platform.OS === 'android' ? {} : {zIndex: PICKER_Z_INDEX},
});

const glyphStyles = StyleSheet.create({
  glyph: {
    position: 'absolute',
    left: GLYPH_INSET,
    top: 0,
    justifyContent: 'center',
    zIndex: PICKER_Z_INDEX + 1,
  },
  // Closed: the wrapper holds only the picker box, so the glyph spans its height.
  glyphClosed: {
    bottom: 0,
  },
  // Open: the box is hidden and the search input sits at the top of the list.
  glyphOpen: {
    height: styles.searchTextInputStyle.maxHeight,
  },
  glyphDisabled: {
    opacity: styles.disabledStyle.opacity,
  },
});
